/**
 * Offline Status Indicator Component
 * Shows current online/offline status and sync state
 */
import { useOffline } from '../contexts/OfflineContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  Cloud,
  CloudOff,
  Check,
  AlertTriangle,
  Database,
  Trash2,
} from 'lucide-react';
import { useState, useEffect } from 'react';

export const OfflineIndicator = () => {
  const {
    isOnline,
    isSyncing,
    queueCount,
    lastSyncTime,
    syncError,
    isInitialized,
    triggerSync,
    getStorageInfo,
    clearCache,
  } = useOffline();

  const [storageInfo, setStorageInfo] = useState(null);

  useEffect(() => {
    const loadStorageInfo = async () => {
      if (!isInitialized) return;
      const info = await getStorageInfo();
      setStorageInfo(info);
    };
    loadStorageInfo();
  }, [getStorageInfo, isInitialized]);

  // Don't render if not initialized yet
  if (!isInitialized) {
    return (
      <Button variant="ghost" size="sm" className="gap-2" disabled>
        <Cloud className="w-4 h-4 text-gray-400" />
      </Button>
    );
  }

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleTimeString();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-2 ${!isOnline ? 'text-amber-500' : ''}`}
          data-testid="offline-indicator"
        >
          {isOnline ? (
            isSyncing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Cloud className="w-4 h-4 text-emerald-500" />
            )
          ) : (
            <CloudOff className="w-4 h-4 text-amber-500" />
          )}
          {queueCount > 0 && (
            <Badge variant="secondary" className="text-xs px-1.5">
              {queueCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-4">
          {/* Status Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <Wifi className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium text-emerald-600">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-amber-600">Offline</span>
                </>
              )}
            </div>
            {isSyncing && (
              <Badge className="bg-blue-500">
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                Syncing
              </Badge>
            )}
          </div>

          {/* Sync Status */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last sync:</span>
              <span>{formatTime(lastSyncTime)}</span>
            </div>
            
            {queueCount > 0 && (
              <div className="flex items-center justify-between text-amber-600">
                <span>Pending changes:</span>
                <span>{queueCount} item(s)</span>
              </div>
            )}

            {syncError && (
              <div className="flex items-center gap-2 text-red-500 text-xs">
                <AlertTriangle className="w-3 h-3" />
                {syncError}
              </div>
            )}
          </div>

          {/* Storage Info */}
          {storageInfo && (
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Database className="w-4 h-4" />
                Offline Storage
              </div>
              <div className="text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Used:</span>
                  <span>{formatBytes(storageInfo.usage)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Available:</span>
                  <span>{formatBytes(storageInfo.quota - storageInfo.usage)}</span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full"
                    style={{ width: `${Math.min(storageInfo.percentUsed, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={triggerSync}
              disabled={!isOnline || isSyncing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync Now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCache}
              title="Clear offline cache"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Offline Mode Info */}
          {!isOnline && (
            <p className="text-xs text-muted-foreground">
              Changes will be saved locally and synced when you're back online.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OfflineIndicator;
